import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyProjectService } from '@/api/services/company-project.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '@/api/errors/error-handler';
import type {
  CompanyProjectFilters,
  CreateCompanyProjectRequest,
  UpdateCompanyProjectRequest,
  UpdateCompanyProjectOrderRequest,
} from '@/api/types';

export const useCompanyProjects = (filters?: CompanyProjectFilters) => {
  return useQuery({
    queryKey: queryKeys.companyProjects.list(filters),
    queryFn: () => companyProjectService.getProjects(filters),
    staleTime: 2 * 60 * 1000,
  });
};

export const useCompanyProject = (id: number) => {
  return useQuery({
    queryKey: queryKeys.companyProjects.detail(id),
    queryFn: () => companyProjectService.getProject(id),
    enabled: !!id,
  });
};

export const useCreateCompanyProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateCompanyProjectRequest | FormData) =>
      companyProjectService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companyProjects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.projects() });
      toast({
        title: t('portfolio.projectAdded') || 'Project Added',
        description: t('portfolio.projectAddedDesc') || 'Project has been added successfully',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.addFailed') || 'Add Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateCompanyProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCompanyProjectRequest | FormData }) =>
      companyProjectService.updateProject(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companyProjects.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyProjects.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.projects() });
      toast({
        title: t('portfolio.projectUpdated') || 'Project Updated',
        description: t('portfolio.projectUpdatedDesc') || 'Project has been updated successfully',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.updateFailed') || 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteCompanyProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => companyProjectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companyProjects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.projects() });
      toast({
        title: t('portfolio.projectDeleted') || 'Project Deleted',
        description: t('portfolio.projectDeletedDesc') || 'Project has been deleted successfully',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.deleteFailed') || 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateCompanyProjectOrder = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: UpdateCompanyProjectOrderRequest) =>
      companyProjectService.updateOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companyProjects.all });
      toast({
        title: t('portfolio.orderUpdated') || 'Order Updated',
        description: t('portfolio.orderUpdatedDesc') || 'Project order has been updated',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.updateFailed') || 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useToggleCompanyProjectActive = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => companyProjectService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companyProjects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.projects() });
      toast({
        title: t('portfolio.statusUpdated') || 'Status Updated',
        description: t('portfolio.statusUpdatedDesc') || 'Project status has been updated',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('portfolio.updateFailed') || 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};
